import { AbpMultiTenancyService } from 'abp-ng2-module';
import { Injectable } from '@angular/core';
import { ApplicationInfoDto, GetCurrentLoginInformationsOutput, RoleInfoDto, SessionServiceProxy, TenantLoginInfoDto, UserLoginInfoDto } from '@shared/service-proxies/service-proxies';
import { FeatureCode } from '@shared/utils/feature-code';
import { ModalPopupService } from '@app/main/cablepro/modal-popup.service';
import { Router } from '@node_modules/@angular/router';
import { environment } from 'environments/environment';
import { WebfacingLoginComponent } from 'webfacing/webfacing-login/webfacing-login.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { WebfacingRegisterComponent } from 'webfacing/webfacing-register/webfacing-register.component';
import posthog from "posthog-js";
import { PosthogService } from '@app/shared/common/posthog.service';
import { MomentHelper } from '@shared/helpers/MomentHelper';

@Injectable()
export class AppSessionService {

    private _user: UserLoginInfoDto;
    private _tenant: TenantLoginInfoDto;
    private _application: ApplicationInfoDto;
    private _role: RoleInfoDto;
    private _helpUrlToken: string;

    constructor(
        private _sessionService: SessionServiceProxy,
        private posthog: PosthogService,
        private _abpMultiTenancyService: AbpMultiTenancyService,
        private modalPopupService: ModalPopupService,
        private router: Router,
        private ngbModalService: NgbModal) {
    }

    get application(): ApplicationInfoDto {
        return this._application;
    }

    get user(): UserLoginInfoDto {
        return this._user;
    }

    get role(): RoleInfoDto {
        return this._role;
    }

    get userId(): number {
        return this.user ? this.user.id : null;
    }

    get tenant(): TenantLoginInfoDto {
        return this._tenant;
    }

    get tenancyName(): string {
        return this._tenant ? this.tenant.tenancyName : '';
    }

    get tenantId(): number {
        return this.tenant ? this.tenant.id : null;
    }

    get helpUrlToken(): string {
        return this._helpUrlToken;
    }

    getShownLoginName(): string {
        const userName = this._user.userName;
        if (!this._abpMultiTenancyService.isEnabled) {
            return userName;
        }

        return (this._tenant ? this._tenant.tenancyName : '.') + '\\' + userName;
    }

    init(isFromWebfacing: boolean = false): Promise<boolean> {
        this.posthog.init();
        return new Promise<boolean>((resolve, reject) => {
            this._sessionService.getCurrentLoginInformations("1", isFromWebfacing).toPromise().then((result: GetCurrentLoginInformationsOutput) => {
                if (result) {
                    this._application = result.application;
                    this._user = result.user;
                    this._tenant = result.tenant;
                    this._role = result.role;
                    this._helpUrlToken = result.helpUrlToken;
                    if (this._user) {
                        this.posthog.identify(this._user);
                    }
                }
                resolve(true);
            }, (err) => {
                reject(err);
            });
        });
    }

    changeTenantIfNeeded(tenantId?: number): boolean {
        if (this.isCurrentTenant(tenantId)) {
            return false;
        }

        abp.multiTenancy.setTenantIdCookie(tenantId);
        location.reload();
        return true;
    }

    private isCurrentTenant(tenantId?: number) {
        if (!tenantId && this.tenant) {
            return false;
        } else if (tenantId && (!this.tenant || this.tenant.id !== tenantId)) {
            return false;
        }

        return true;
    }

    hasPermission(code: FeatureCode, calculationTypeId: number = 0): boolean {
        const feature = this.user?.userPlanFeatures?.features?.find(f => f.featureCode === code && (calculationTypeId === 0 || f.calculationTypeId === calculationTypeId));

        if (!feature) {
            return false; // feature not available for user
        }

        // If Allow/Deny type
        if (feature.isAllowed !== undefined) {
            return feature.isAllowed;
        }

        // If LimitBased type (check remaining quota)
        // if (feature.limit !== undefined) {
        //     return feature.used !== undefined
        //         ? feature.used < feature.limit   // still has quota
        //         : feature.limit > 0;             // default check
        // }

        return false;
    }

    getFeatureLimit(code: FeatureCode): number {
        const feature = this.user?.userPlanFeatures?.features?.find(f => f.featureCode === code);

        if (!feature) {
            return 0; // feature not available for user
        }

        if (feature.limit !== undefined) {
            return feature.limit;
        }

        return 0;
    }

    getErrorMessage(code: FeatureCode): string {
        if (!this.hasPermission(code)) {
            return 'You do not have permission to use this feature.';
        }
        return '';
    }

    public showUpgradeAlert(title?: string, message?: string) {
        if (!title)
            title = 'Unlock this Feature';
        if (!message)
            message = 'Please upgrade your plan to gain access to this feature.';
        const isEligibleForTrial = this.isEligibleForTrial();
        this.modalPopupService.confirm(
            title,
            message,
            (value) => {
                if (value == true) {
                    this.UpgradePlan();
                }
                else if (value?.additional == true) {
                    this.UpgradePlan(true);
                }
            },
            '',
            'Manage Plan',
            'btn-primary',
            'Cancel',
            'btn-secondary', false, true, isEligibleForTrial, isEligibleForTrial ? "Start Free Trial" : undefined
        );
    }
    public UsageLimitReached(title?: string, message?: string, isprofessional: boolean = false) {
        const isWebFacing = window.location.hostname === environment.WebFacingHost;
        const isEligibleForTrial = this.isEligibleForTrial();
        if (isWebFacing && this.user?.isWebFacingUser) {
            this.modalPopupService.confirm("Guest Usage Limit Reached", "Get double the calculation credits instantly by logging in or signing up.", (value) => {
                if (value) {
                    this.ngbModalService.open(WebfacingLoginComponent, {
                        centered: true,
                        backdrop: 'static',
                        keyboard: false,
                        animation: true
                    });
                }
                else if (value == false) {

                    this.ngbModalService.open(WebfacingRegisterComponent, {
                        centered: true,
                        backdrop: 'static',
                        keyboard: false,
                        animation: true
                    });
                }
            }, "", "Log In", "btn-primary", "Sign Up", "", false, true);
        }
        else {
            if (isprofessional == true) {
                this.modalPopupService.confirm(
                    title,
                    message,
                    (value) => {
                        if (value) {
                            if (isWebFacing) {
                                this.sendUpgradeEmail();
                            }
                            else {
                                $(".clsscreenshot").hide();
                                $("#contactusDlg").modal("show");
                            }
                        }
                        else {

                        }
                    },
                    '',
                    'Contact Sales',
                    'btn-primary',
                    'Cancel',
                    'btn-secondary'
                );
            }
            else {

                this.modalPopupService.confirm(
                    title,
                    message,
                    (value) => {
                        if (value == true) {
                            this.UpgradePlan();
                        }
                        else if (value?.additional == true) {
                            this.UpgradePlan(true);
                        }
                        else if (value == false) {
                            if (isWebFacing) {
                                this.sendUpgradeEmail();
                            }
                            else {
                                $(".clsscreenshot").hide();
                                $("#contactusDlg").modal("show");
                            }
                        }

                    },
                    '',
                    'Manage Plan',
                    'btn-primary',
                    'Contact Sales',
                    'btn-secondary', false, true, isEligibleForTrial, isEligibleForTrial ? "Start Free Trial" : undefined
                );
            }
        }

    }

    isEligibleForTrial(): boolean {

        const hasPaidSubscription = !!this.user.userStripePlanId && this.user.userStripePlanId.trim() !== '' && this.user.userStripeSubscriptionStatus && this.user.userStripeSubscriptionStatus.toLowerCase() === 'active';

        if (hasPaidSubscription) {
            // ❌ Not eligible if already had a paid plan
            return false;
        }

        let hasManuallyPaidSubscription = this.user.isPlanAddedManually && this.user.userStripePlanId && this.user.userStripePlanId.trim() !== '' && !MomentHelper.IsSubscriptionDateExpired(this.user.expirationDate);
        if (hasManuallyPaidSubscription) {
            // ❌ Not eligible if already had a paid plan manually added
            return false;
        }

        if (!this.user.trialStartDate) {
            // Never took trial
            return true;
        }

        const trialStart = new Date(this.user.trialStartDate);
        const today = new Date();

        // Calculate the difference in days
        const diffDays = (today.getTime() - trialStart.getTime()) / (1000 * 60 * 60 * 24);

        return diffDays > 90; // Eligible if more than 90 days since trial started
    }


    sendUpgradeEmail(): void {
        const userName = this.user.userName;
        const currentPlan = this.user.userStripePlanName || '';
        const planDuration = this.user.userStripePlanInterval || ''; // e.g., 'Monthly' or 'Yearly'

        // Normalize and check if duration already exists in plan name
        const durationRegex = new RegExp(planDuration, 'i'); // case-insensitive check
        const displayPlan = currentPlan
            ? durationRegex.test(currentPlan)
                ? currentPlan // already contains 'Monthly' or 'Yearly'
                : `${currentPlan} (${planDuration == 'year' ? 'Yearly' : 'Monthly'})` // append only if not present
            : 'Free';

        const email = 'sales@elek.com';
        const subject = encodeURIComponent('Upgrade My Cable Pro Web Subscription Plan');
        const body = encodeURIComponent(
            `Hi ELEK Sales Team,
            
I want to upgrade my Cable Pro Web subscription to get more features and higher usage limits.

My account details:
Username: ${userName}
Current Plan: ${displayPlan}

Please share the available upgrade options and next steps.

Thanks,
${this.user.fullName}`
        );

        const mailtoLink = `mailto:${email}?subject=${subject}&body=${body}`;
        window.location.href = mailtoLink;
    }


    UpgradePlan(trialEnabled: boolean = false) {
        $('.modal').modal('hide');
        $('.modal-backdrop').remove();
        this.modalPopupService.closeAll();
        this.GoToPricing(trialEnabled);
    }

    GoToPricing(trialEnabled: boolean = false): void {
        const isWebFacing = location.hostname === environment.WebFacingHost;
        const trialParam = trialEnabled ? '?trial=true' : '';
        if (isWebFacing) {
            if (location.hostname === "localhost") {
                location.href = "/pricing/" + trialParam;
            }
            else {
                location.href = "/calculators/pricing/" + trialParam;
            }
            return;
        }
        // Angular internal navigation
        this.router.navigate(
            ['/app/main/', 'pricing'],
            { queryParams: { trial: trialEnabled } }
        );
    };

}
